// MomentoEspecialYoutubeAudio — instância dedicada e isolada da YouTube
// IFrame API, usada só durante a celebração de aniversário quando a fonte
// configurada é YouTube. Nunca reaproveita o YoutubeSlide da playlist (que
// tem uma invariante de ciclo de vida documentada em CLAUDE.md incompatível
// com este uso pontual e raro) — cria e destrói sua própria instância a
// cada disparo, o que é seguro aqui porque não há troca de vídeo em alta
// frequência (o motivo daquela invariante).

import { useEffect, useRef } from 'react';
import { carregarYouTubeAPI, type YTPlayerInstance } from '@/shared/lib/youtubeIframeApi';

const POLL_MS = 300;

interface Props {
  videoId:   string;
  inicioSeg: number;
  fimSeg:    number;
  /** Espelha o gesto de desbloqueio de áudio já capturado pelo Player nesta sessão. */
  unlocked:  boolean;
  /** Chamado uma vez quando o polling detecta que a reprodução atingiu fimSeg. */
  onFimAlcancado?: () => void;
}

export default function MomentoEspecialYoutubeAudio({ videoId, inicioSeg, fimSeg, unlocked, onFimAlcancado }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef     = useRef<YTPlayerInstance | null>(null);
  const pollRef       = useRef<ReturnType<typeof setInterval> | null>(null);
  const destroyedRef  = useRef(false);
  const onFimAlcancadoRef = useRef(onFimAlcancado);
  useEffect(() => { onFimAlcancadoRef.current = onFimAlcancado; });

  const unlockedRef = useRef(unlocked);
  useEffect(() => { unlockedRef.current = unlocked; });

  useEffect(() => {
    destroyedRef.current = false;
    const inicio = Math.max(0, Math.floor(inicioSeg));

    carregarYouTubeAPI().then(() => {
      if (destroyedRef.current || !containerRef.current || !window.YT?.Player) return;

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        width:  1,
        height: 1,
        playerVars: {
          autoplay: 1,
          controls: 0,
          mute:     unlockedRef.current ? 0 : 1,
        },
        events: {
          onReady(e) {
            if (destroyedRef.current) return;
            e.target.loadVideoById(videoId, inicio);
            if (unlockedRef.current) {
              e.target.unMute();
              e.target.setVolume(100);
            }
            e.target.playVideo();

            pollRef.current = setInterval(() => {
              const atual = playerRef.current?.getCurrentTime?.() ?? 0;
              if (atual >= fimSeg) {
                playerRef.current?.pauseVideo();
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                onFimAlcancadoRef.current?.();
              }
            }, POLL_MS);
          },
          onError() { /* falha silenciosa — overlay visual segue independente do áudio */ },
        },
      });
    });

    return () => {
      destroyedRef.current = true;
      if (pollRef.current) clearInterval(pollRef.current);
      try { playerRef.current?.destroy(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, [videoId, inicioSeg, fimSeg]);

  // 1x1 fora da tela — só o áudio importa; o visual da celebração é o
  // OverlayAniversario, que já cobre a tela inteira por cima (z-[100]).
  return (
    <div
      ref={containerRef}
      className="fixed bottom-0 right-0 h-px w-px opacity-0 pointer-events-none"
      aria-hidden="true"
    />
  );
}
